import type { DigitalProposalTier } from "./digitalProposal";

export interface BrochureItem {
  id: string;
  manufacturer: string;
  product_line: string;
  tier: "good" | "better" | "best";
  brochure_url: string;
  thumbnail_url?: string;
}

export interface CertificationItem {
  name: string;
  logo_url?: string;
}

export interface FinancingLender {
  name: string;
  logo_url?: string;
  apply_url?: string;
}

export interface DigitalProposalTemplate {
  id: string;
  org_id: string;

  // Branding
  company_name?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  hero_photo_url?: string | null;
  hero_eyebrow?: string | null;
  hero_subheadline?: string | null;

  // About
  about_headline?: string | null;
  about_body?: string | null;
  years_in_business?: number | null;
  license_number?: string | null;
  certifications: CertificationItem[];
  team_photo_url?: string | null;

  // Brochures
  brochures: BrochureItem[];

  // Default tiers (auto-fill new proposals)
  default_tiers: DigitalProposalTier[];

  // Financing
  financing_enabled: boolean;
  financing_lenders: FinancingLender[];
  financing_apply_url?: string | null;

  // Terms / signature
  default_terms?: string | null;
  deposit_required: boolean;
  deposit_percent?: number | null;

  created_at: string;
  updated_at: string;
}

/** Curated industry brochures preloaded for new orgs. */
export const PRELOADED_BROCHURES: Omit<BrochureItem, "id">[] = [
  // GAF
  { manufacturer: "GAF", product_line: "Royal Sovereign (3-Tab)", tier: "good", brochure_url: "https://www.gaf.com/en-us/document-library/documents/productdocuments/residentialroofingsystemsdocuments/strip-shingles/royalsovereignshinglesalessheetressr200.pdf" },
  { manufacturer: "GAF", product_line: "Timberline HDZ", tier: "better", brochure_url: "https://www.gaf.com/en-us/document-library/documents/productdocuments/residentialroofingsystemsdocuments/architectural-shingles/timberlinehdzshinglesalessheetreshdzsa.pdf" },
  { manufacturer: "GAF", product_line: "Grand Sequoia / Camelot II", tier: "best", brochure_url: "https://www.gaf.com/en-us/document-library/documents/productdocuments/residentialroofingsystemsdocuments/designer-shingles/camelotiidesignershinglesalessheetrescasa.pdf" },
  // CertainTeed
  { manufacturer: "CertainTeed", product_line: "XT 25 (3-Tab)", tier: "good", brochure_url: "https://www.certainteed.com/resources/XT25_Brochure.pdf" },
  { manufacturer: "CertainTeed", product_line: "Landmark", tier: "better", brochure_url: "https://www.certainteed.com/resources/Landmark_Brochure.pdf" },
  { manufacturer: "CertainTeed", product_line: "Presidential Shake", tier: "best", brochure_url: "https://www.certainteed.com/resources/Presidential_Shake_Brochure.pdf" },
  // Owens Corning
  { manufacturer: "Owens Corning", product_line: "Supreme (3-Tab)", tier: "good", brochure_url: "https://www.owenscorning.com/en-us/roofing/shingles/supreme" },
  { manufacturer: "Owens Corning", product_line: "Duration", tier: "better", brochure_url: "https://www.owenscorning.com/en-us/roofing/shingles/trudefinition-duration" },
  { manufacturer: "Owens Corning", product_line: "Berkshire / Woodmoor", tier: "best", brochure_url: "https://www.owenscorning.com/en-us/roofing/shingles/berkshire" },
  // Atlas
  { manufacturer: "Atlas", product_line: "Pinnacle Pristine", tier: "better", brochure_url: "https://www.atlasroofing.com/document/pinnacle-pristine-brochure" },
  { manufacturer: "Atlas", product_line: "StormMaster Shake", tier: "best", brochure_url: "https://www.atlasroofing.com/document/stormmaster-shake-brochure" },
  // Malarkey
  { manufacturer: "Malarkey", product_line: "Vista", tier: "better", brochure_url: "https://www.malarkeyroofing.com/wp-content/uploads/Vista-Brochure.pdf" },
  { manufacturer: "Malarkey", product_line: "Legacy", tier: "best", brochure_url: "https://www.malarkeyroofing.com/wp-content/uploads/Legacy-Brochure.pdf" },
];

export const PRELOADED_LENDERS: FinancingLender[] = [
  { name: "Hearth", apply_url: "https://www.gethearth.com/" },
  { name: "GreenSky", apply_url: "https://www.greensky.com/" },
  { name: "Service Finance", apply_url: "https://www.svcfin.com/" },
];
